/**
 * Beta Framework Components — badges, disclaimers, feedback
 *
 * Used to label and protect advanced features with proper acknowledgments.
 */

import { useState, useEffect } from 'react';
import { X, Loader2, AlertTriangle, BookOpen, MessageSquare, Sparkles, Check } from 'lucide-react';
import { acknowledgeBetaFeature, submitBetaFeedback, getMyAcknowledgments, getBetaFeatures } from '../services/api';

const BADGE_STYLES = {
  amber: { bg: 'bg-amber-400/15', text: 'text-amber-400', border: 'border-amber-400/25' },
  red: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/25' },
  purple: { bg: 'bg-violet-400/15', text: 'text-violet-400', border: 'border-violet-400/25' },
};

// ─── BetaBadge ──────────────────────────────────────────────────────────────

export function BetaBadge({ feature, size = 'small' }) {
  if (!feature) return null;
  const style = BADGE_STYLES[feature.badgeColor] || BADGE_STYLES.amber;
  const sizing = size === 'small'
    ? 'text-[9px] px-2 py-0.5'
    : 'text-[11px] px-3 py-1';
  return (
    <span className={`${sizing} font-bold rounded-full ${style.bg} ${style.text} border ${style.border} tracking-wider uppercase`} title={feature.description}>
      {feature.badge}
    </span>
  );
}

// ─── FeatureDisclaimerModal ─────────────────────────────────────────────────

export function FeatureDisclaimerModal({ feature, onAcknowledge, onCancel }) {
  const [scrolled, setScrolled] = useState(false);
  const [warningChecked, setWarningChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function handleScroll(e) {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 20) setScrolled(true);
  }

  const requiresWarning = feature?.disclaimer?.warningRequired;
  const canProceed = scrolled && (!requiresWarning || warningChecked);

  async function handleAck() {
    setSubmitting(true);
    try { await acknowledgeBetaFeature(feature.id); onAcknowledge(); }
    catch (err) { alert(err.message); }
    setSubmitting(false);
  }

  if (!feature) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/80" />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-dark-700/50">
          <div className="flex items-center gap-3 mb-2">
            <BetaBadge feature={feature} size="medium" />
            <span className="text-gray-500 text-xs">Requires {feature.requiresTier} tier+</span>
          </div>
          <h2 className="text-xl font-bold text-white">{feature.name}</h2>
          <p className="text-gray-400 text-sm mt-1">{feature.description}</p>
        </div>

        <div className="p-5 flex-1 overflow-hidden flex flex-col">
          <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Before you use this feature
          </h3>
          <div onScroll={handleScroll}
            className="flex-1 overflow-y-auto bg-dark-700/30 rounded-xl p-4 border border-dark-600/30 text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">
            {feature.disclaimer.full}
          </div>
          <p className="text-[10px] text-gray-500 mt-2">
            {scrolled ? '✓ You have read the full disclaimer' : '↓ Scroll to read the full disclaimer'}
          </p>

          {requiresWarning && (
            <label className="flex items-start gap-2 mt-3 bg-red-500/5 rounded-xl p-3 border border-red-500/15">
              <input type="checkbox" checked={warningChecked} onChange={e => setWarningChecked(e.target.checked)} disabled={!scrolled}
                className="mt-0.5 w-4 h-4 accent-red-400" />
              <span className="text-red-300 text-xs">I understand this is an experimental feature with significant risk and I accept all risks described above.</span>
            </label>
          )}

          {feature.helpArticleId && (
            <a href={`/help-center?article=${feature.helpArticleId}`} target="_blank" rel="noreferrer"
              className="mt-3 text-amber-400 text-xs hover:text-amber-300 flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" /> Read the complete guide first
            </a>
          )}
        </div>

        <div className="p-5 border-t border-dark-700/50 flex gap-2">
          <button onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-dark-700 text-gray-300 hover:bg-dark-600">
            Cancel
          </button>
          <button onClick={handleAck} disabled={!canProceed || submitting}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-amber-500 text-dark-900 hover:bg-amber-400 disabled:opacity-40 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            I understand, continue
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PersistentDisclaimerBanner ─────────────────────────────────────────────

export function PersistentDisclaimerBanner({ feature }) {
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem(`disclaimer_dismissed_${feature?.id}`) === 'true'
  );
  if (dismissed || !feature) return null;
  return (
    <div className="bg-amber-500/5 rounded-xl p-3 border border-amber-500/15 flex items-start gap-3 mb-4">
      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-amber-300 text-xs">{feature.disclaimer.short}</p>
        {feature.helpArticleId && (
          <a href={`/help-center?article=${feature.helpArticleId}`} target="_blank" rel="noreferrer"
            className="text-amber-400 text-[10px] hover:text-amber-300 inline-flex items-center gap-1 mt-1">
            <BookOpen className="w-3 h-3" /> Learn more
          </a>
        )}
      </div>
      <button onClick={() => { sessionStorage.setItem(`disclaimer_dismissed_${feature.id}`, 'true'); setDismissed(true); }}
        className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
    </div>
  );
}

// ─── BetaFeedbackButton ─────────────────────────────────────────────────────

export function BetaFeedbackButton({ feature }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function submit() {
    if (!rating) return;
    setSubmitting(true);
    try {
      await submitBetaFeedback(feature.id, rating, comment);
      setDone(true);
      setTimeout(() => { setOpen(false); setDone(false); setRating(null); setComment(''); }, 1500);
    } catch (err) { alert(err.message); }
    setSubmitting(false);
  }

  if (!feature) return null;

  return (
    <>
      <button onClick={() => setOpen(true)} title="Beta feedback"
        className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-violet-500 text-white shadow-2xl hover:bg-violet-400 flex items-center justify-center transition-all">
        <MessageSquare className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-400" /> Beta Feedback: {feature.name}</h3>
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {done ? (
              <div className="text-center py-8">
                <Check className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
                <p className="text-emerald-400 font-bold">Thank you!</p>
                <p className="text-gray-500 text-xs mt-1">Your feedback helps us improve.</p>
              </div>
            ) : (
              <>
                <p className="text-gray-400 text-xs mb-3">How is this feature working for you?</p>
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setRating(n)}
                      className={`py-3 rounded-xl text-2xl ${rating === n ? 'bg-violet-500/20 ring-2 ring-violet-400/50' : 'bg-dark-700 hover:bg-dark-600'}`}>
                      {['😞', '😕', '😐', '🙂', '😍'][n - 1]}
                    </button>
                  ))}
                </div>
                <textarea value={comment} onChange={e => setComment(e.target.value)} maxLength={1000} rows={4}
                  placeholder="What's working? What's not? What would you like to see?"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none mb-3" />
                <p className="text-[10px] text-gray-600 mb-3">💡 Active feedback contributors get early access to new beta features.</p>
                <button onClick={submit} disabled={!rating || submitting}
                  className="w-full py-3 rounded-xl text-sm font-bold bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-40 flex items-center justify-center gap-2">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Feedback'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── useBetaGate hook ───────────────────────────────────────────────────────

export function useBetaGate(featureId) {
  const [feature, setFeature] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const [features, acks] = await Promise.all([getBetaFeatures(), getMyAcknowledgments()]);
        if (cancelled) return;
        const f = (features.features || []).find(x => x.id === featureId.toLowerCase() || x.id === featureId);
        if (!f) {
          setError('Feature not registered');
          setLoading(false);
          return;
        }
        setFeature(f);
        setAcknowledged(!!acks.acknowledgments?.[f.id]);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, [featureId]);

  function markAcknowledged() { setAcknowledged(true); }

  return { feature, acknowledged, loading, error, markAcknowledged };
}
